import { Box, InputAdornment, TextField, Typography } from "@mui/material";

const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  fullWidth = true,
  required = false,
  disabled = false,
  error = false,
  helperText = "",
  multiline = false,
  rows,
  InputProps,
  disallowSpecialCharacters = true,
  endAdornment = false,
  endAdornmentIconPath,
  endAdornmentIconClick,
  ...props
}) => {
  const handleChange = (e) => {
    const { value, type } = e.target;

    if (type === "tel") {
      const phoneValue = value.replace(/[^0-9\s\-()+.]/g, "");
      if (phoneValue !== value) {
        e.target.value = phoneValue;
      }
      onChange(e);
      return;
    }

    if (type === "number") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue !== value) {
        e.target.value = numericValue;
        onChange(e);
        return;
      }
    }

    onChange(e);
  };

  const handleKeyDown = (e) => {
    if (type === "number" && disallowSpecialCharacters) {
      // Disallow special characters
      if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label ? (
        <Typography
          color="rgba(47, 43, 61, 0.9)"
          sx={{ fontSize: "15px", fontWeight: 500 }}
        >
          {label}
        </Typography>
      ) : null}
      <TextField
        name={name}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        type={type}
        placeholder={placeholder}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        multiline={multiline}
        rows={rows}
        variant="outlined"
        size="small"
        inputProps={{ maxLength: props.maxLength, dir: props.dir }}
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: "8px",
            padding: "0px",
            "&.Mui-disabled": {
              backgroundColor: "rgba(248, 247, 250, 1)",
            },
            // Remove number input arrows
            ...((type === "number" || type === "tel") && {
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }),
            ...props?.customSx?.inputBaseRoot,
          },
          "& .MuiInputBase-input": {
            fontSize: "15px",
            color: "rgba(47, 43, 61, 0.9)",
            padding: "8px 12px",
            height: "auto",
            ...props?.customSx?.input,
          },
          "& .MuiInputLabel-root": {
            fontSize: "15px",
            color: "rgba(47, 43, 61, 0.9)",
            fontWeight: 500,
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "rgba(230, 228, 235, 1)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(36, 139, 112, 1)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "rgba(36, 139, 112, 1)",
            },
            "&.Mui-disabled fieldset": {
              borderColor: "rgba(230, 228, 235, 1)",
            },
            "&.Mui-disabled:hover fieldset": {
              borderColor: "rgba(230, 228, 235, 1)",
            },
          },
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            fontSize: "13px",
          },
        }}
        InputProps={{
          ...InputProps,
          endAdornment: endAdornment ? (
            <InputAdornment
              onClick={endAdornmentIconClick}
              position="end"
              sx={{ ml: 0, mr: "8px" }}
            >
              <img src={endAdornmentIconPath} alt="icon" />
            </InputAdornment>
          ) : null,
          sx: {
            "& input::placeholder": {
              color: "rgba(47, 43, 61, 0.5)",
              opacity: 1,
            },
            ...(InputProps?.sx || {}),
          },
        }}
        {...props}
      />
    </Box>
  );
};

export default InputField;
